// src/pages/VerifyEmailPage.js
// Updated: 10/31/2025 03:22 PM EDT
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sendEmailVerification } from 'firebase/auth';
import AuthLayout from '../components/AuthLayout';
import { Mail, LogOut } from 'lucide-react';

export default function VerifyEmailPage() {
  const { currentUser, logout, isLoading } = useAuth();
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleResend = async () => {
    if (!currentUser) return;
    setIsSending(true);
    setError('');
    setSuccess('');
    try {
      await sendEmailVerification(currentUser);
      setSuccess('A new verification email has been sent.');
    } catch (err) {
      console.error('Email Resend Error:', err);
      setError('Failed to resend email. Please try again in a moment.');
    } finally {
      setIsSending(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      setError('Failed to log out.');
    }
  };

  if (isLoading || !currentUser) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="h-16 w-16 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
      </div>
    );
  }

  return (
    <AuthLayout title="Verify Your Email">
      <div className="text-center">
        <Mail className="mx-auto h-12 w-12 text-blue-600" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">
          Check your inbox!
        </h3>
        <div className="mt-2 text-sm text-gray-600">
          <p>
            We've sent a verification link to
            <strong className="mx-1">{currentUser.email}</strong>
          </p>
          <p className="mt-2">
            Please click the link in that email to continue.
          </p>
          {/* --- THIS IS THE NEWLY ADDED LINE --- */}
          <p className="mt-4 text-xs text-gray-500">
            If you don't see it, please check your spam or junk folder.
          </p>
          {/* --- END OF NEW LINE --- */}
        </div>

        {error && <div className="mt-4 text-sm text-red-600">{error}</div>}
        {success && <div className="mt-4 text-sm text-green-600">{success}</div>}

        <div className="mt-6 space-y-4">
          <button
            type="button"
            onClick={handleResend}
            disabled={isSending}
            className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {isSending ? 'Sending...' : 'Resend Verification Email'}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full justify-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <LogOut className="mr-2 h-5 w-5" />
            Log Out
          </button>
        </div>
        <p className="mt-6 text-xs text-gray-500">
          Already verified?{' '}
          <Link
            to="/login"
            onClick={handleLogout}
            className="font-medium text-blue-600 hover:text-blue-500"
          >
            Log in again
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
